// Markdown treats any run of blank lines as one paragraph break, so an empty line typed for spacing is lost
// on every surface and gone from the editor the next time it opens. Each one is saved as a paragraph holding
// only `&nbsp;` instead - the one form that survives Markdown in Teams, email and on the web.

export const BLANK_LINE = '&nbsp;';

/** Marks a line the editor added beside a picture so there is somewhere to put the cursor. */
export const EDITOR_ONLY_LINE = 'editorOnlyLine';

interface RawBlock {
  type: string;
  text: string;
  data?: { [key: string]: unknown };
}

const isEmptyLine = (block: RawBlock) => block.type === 'unstyled' && block.text === '';

/**
 * Before saving: every empty line between two pieces of content becomes `&nbsp;`, so what the editor shows is
 * what everyone else gets. Leading and trailing empty lines are left alone (they carry no spacing), and so is a
 * line the editor added itself beside a picture while it is still empty - saving that would add another blank
 * line every time the answer was saved.
 */
export const encodeBlankLines = <T extends RawBlock>(blocks: T[]): T[] => {
  const hasContent = blocks.map(b => !isEmptyLine(b));
  const first = hasContent.indexOf(true);
  const last = hasContent.lastIndexOf(true);
  return blocks.map((block, i) =>
    isEmptyLine(block) && i > first && i < last && !block.data?.[EDITOR_ONLY_LINE]
      ? { ...block, text: BLANK_LINE }
      : block);
};

/** After loading: a line the parser read as a lone non-breaking space was a saved empty line. */
export const decodeBlankLines = <T extends RawBlock>(blocks: T[]): T[] =>
  blocks.map(block => (block.type === 'unstyled' && block.text === ' ' ? { ...block, text: '' } : block));
